import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Package, PencilIcon, TrashIcon, XIcon } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import type {
  BaseOptionType,
  SelectValue as SelectValueType,
} from 'components/Select';
import { Input } from 'components/input';
import { RHFCommitOnBlurNumberInput } from 'components/input/RHFCommitOnBlurNumberInput';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { calculateLTLFreightClass } from 'lib/api/calculateLTLFreightClass';
import {
  LTLItem,
  LTL_DIMENSION_UNIT_LABEL_MAP,
  LTL_FREIGHT_CLASSES,
  LTL_WEIGHT_UNIT_LABEL_MAP,
  LTL_WEIGHT_UNIT_OPTIONS,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { TMSCommodity } from 'types/Load';
import { calculateDensity } from 'utils/calculateDensity';

import { useQuickQuoteContext } from '../../context/QuickQuoteContext';
import { getColorForItemId } from '../components/visualizationColors';
import { useIs3DVisualisationEnabled } from '../hooks/useLTLQuickQuoteConfigFlags';
import {
  useAutoOpenSplitSideLoadRow,
  useSideLoadItemStatusTag,
} from '../hooks/useSideLoadItemStatusTag';
import { useTurvoCommodities } from '../hooks/useTurvoCommodities';
import {
  ITEM_CARD_ANIMATIONS,
  TURVO_DIM_UNIT_TO_FORM_DIMENSION_UNIT,
  TURVO_QTY_UNIT_DEFAULT,
  TURVO_QTY_UNIT_LABEL_BY_KEY,
  TURVO_QTY_UNIT_OPTIONS,
  TURVO_QTY_UNIT_TYPE_ID_TO_VALUE,
  TURVO_WEIGHT_UNIT_TO_FORM_WEIGHT_UNIT,
} from './constants';
import {
  convertDimensionToFeet,
  convertWeightToPounds,
  isValidNumber,
} from './utils';

interface ItemCardProps {
  index: number;
  itemId: string;
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  itemColorMap: ReadonlyMap<string, string>;
  onRemove: () => void;
}

const FIELD_PATH = 'ltl.items' as const;

type DescriptionOption = BaseOptionType & {
  commodityExternalTmsId?: string;
  isCustom?: boolean;
  renderLabel?: string;
};

/** LTL item card: name, dimensions L×W×H, weight, qty, freight class, nmfc code */
export function ItemCard({
  index,
  formMethods,
  itemColorMap,
  onRemove,
}: ItemCardProps): JSX.Element {
  const { watch, setValue, control } = formMethods;
  const { ltlCustomer } = useQuickQuoteContext();
  const showVisualizationColor = useIs3DVisualisationEnabled();
  const [isEditing, setIsEditing] = useState(false);
  const [descriptionSearchValue, setDescriptionSearchValue] = useState('');
  const [isCalculatingFreightClass, setIsCalculatingFreightClass] =
    useState(false);
  const [originalValues, setOriginalValues] = useState<Partial<LTLItem> | null>(
    null
  );
  const freightClassRequestIdRef = useRef(0);
  const autoAppliedCommodityIdRef = useRef('');
  const watchedItem = watch(`${FIELD_PATH}.${index}`);
  const { renderSideLoadStatusTag } = useSideLoadItemStatusTag({
    control,
    itemIndex: index,
    parentItemLabelPrefix: 'Item',
  });
  const isEmptyItem = !watchedItem?.description;

  const watchedLength = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.length`,
  });
  const watchedWidth = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.width`,
  });
  const watchedHeight = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.height`,
  });
  const watchedHandlingUnits = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.handlingUnits`,
  });
  const watchedWeight = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.weight`,
  });
  const watchedDimensionUnit = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.dimensionUnit`,
  });
  const watchedWeightUnit = useWatch({
    control,
    name: `${FIELD_PATH}.${index}.weightUnit`,
  });

  // Dimension/weight units for LTL items
  const dimensionUnitValue = watchedDimensionUnit ?? 'Inch';
  const weightUnitValue = watchedWeightUnit ?? 'Pound';

  const dimensionUnit =
    LTL_DIMENSION_UNIT_LABEL_MAP[dimensionUnitValue] ?? 'in';
  const weightUnit = LTL_WEIGHT_UNIT_LABEL_MAP[weightUnitValue] ?? 'lb';
  const descriptionValue = watchedItem?.description?.trim() ?? '';
  const {
    commodities,
    isLoading: isLoadingCommodities,
    hasCustomer,
    hasTmsId,
    hasCustomerId,
  } = useTurvoCommodities();

  const densityValue = useMemo(() => {
    const length = watchedLength;
    const width = watchedWidth;
    const height = watchedHeight;
    const quantity = watchedHandlingUnits;
    const totalWeight = watchedWeight;

    if (
      !isValidNumber(length) ||
      !isValidNumber(width) ||
      !isValidNumber(height) ||
      !isValidNumber(quantity) ||
      !isValidNumber(totalWeight)
    ) {
      return '';
    }

    const lengthFeet = convertDimensionToFeet(length, dimensionUnitValue);
    const widthFeet = convertDimensionToFeet(width, dimensionUnitValue);
    const heightFeet = convertDimensionToFeet(height, dimensionUnitValue);
    const weightPounds = convertWeightToPounds(totalWeight, weightUnitValue);

    const density = calculateDensity(
      weightPounds,
      1, // lbs
      lengthFeet,
      widthFeet,
      heightFeet,
      1, // feet
      quantity
    );

    return density != null ? density.toFixed(2) : '';
  }, [
    watchedLength,
    watchedWidth,
    watchedHeight,
    watchedHandlingUnits,
    watchedWeight,
    dimensionUnitValue,
    weightUnitValue,
  ]);

  const freightClassCalculationRequest = useMemo(() => {
    const externalCustomerIdRaw =
      ltlCustomer?.externalId ?? ltlCustomer?.customerId ?? '';
    const externalCustomerId = Number(externalCustomerIdRaw);
    const qty = Number(watchedHandlingUnits);
    const length = Number(watchedLength);
    const width = Number(watchedWidth);
    const height = Number(watchedHeight);
    const weight = Number(watchedWeight);
    const dimensionUnits =
      LTL_DIMENSION_UNIT_LABEL_MAP[dimensionUnitValue] ?? 'in';
    const weightUnits = LTL_WEIGHT_UNIT_LABEL_MAP[weightUnitValue] ?? 'lb';

    const isValid =
      Number.isFinite(externalCustomerId) &&
      externalCustomerId > 0 &&
      Number.isFinite(qty) &&
      qty > 0 &&
      Number.isFinite(length) &&
      length > 0 &&
      Number.isFinite(width) &&
      width > 0 &&
      Number.isFinite(height) &&
      height > 0 &&
      Number.isFinite(weight) &&
      weight > 0;

    if (!isValid) {
      return null;
    }

    return {
      externalCustomerId,
      qty: Math.round(qty),
      dimensions: {
        length,
        width,
        height,
        units: dimensionUnits,
      },
      weight: {
        weight,
        unit: weightUnits,
      },
    };
  }, [
    ltlCustomer?.customerId,
    ltlCustomer?.externalId,
    watchedHandlingUnits,
    watchedLength,
    watchedWidth,
    watchedHeight,
    watchedWeight,
    dimensionUnitValue,
    weightUnitValue,
  ]);

  useEffect(() => {
    const requestId = ++freightClassRequestIdRef.current;

    if (!freightClassCalculationRequest) {
      setIsCalculatingFreightClass(false);
      return;
    }

    setIsCalculatingFreightClass(true);

    const timeoutId = window.setTimeout(async () => {
      const result = await calculateLTLFreightClass(
        freightClassCalculationRequest
      );

      if (requestId !== freightClassRequestIdRef.current) {
        return;
      }

      setIsCalculatingFreightClass(false);

      if (result.isErr()) {
        return;
      }

      const calculatedFreightClass = result.value.value?.trim();
      if (!calculatedFreightClass) {
        return;
      }

      setValue(`${FIELD_PATH}.${index}.freightClass`, calculatedFreightClass, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [freightClassCalculationRequest, index, setValue]);

  const applyCommoditySelection = (
    commodity: TMSCommodity,
    options?: { preserveExisting?: boolean }
  ) => {
    const preserveExisting = options?.preserveExisting ?? false;
    const description =
      commodity.commodityDescription ?? commodity.commodity ?? '';
    const freightClass = commodity.freightClass?.trim() ?? '';
    const nmfcCode = (commodity.nmfc ?? commodity.nmfcNumber ?? '').trim();

    setValue(`${FIELD_PATH}.${index}.description`, description, {
      shouldValidate: true,
    });
    setValue(
      `${FIELD_PATH}.${index}.commodityExternalTmsId`,
      commodity.externalTMSID ?? '',
      {
        shouldValidate: true,
      }
    );

    if (
      !preserveExisting ||
      (freightClass && !watchedItem?.freightClass?.trim())
    ) {
      setValue(`${FIELD_PATH}.${index}.freightClass`, freightClass, {
        shouldDirty: true,
      });
    }

    if (!preserveExisting || (nmfcCode && !watchedItem?.nmfcCode?.trim())) {
      setValue(`${FIELD_PATH}.${index}.nmfcCode`, nmfcCode, {
        shouldDirty: true,
      });
    }

    if (
      commodity.length &&
      (!preserveExisting || !isValidNumber(watchedLength))
    ) {
      setValue(`${FIELD_PATH}.${index}.length`, commodity.length, {
        shouldDirty: true,
      });
    }
    if (
      commodity.width &&
      (!preserveExisting || !isValidNumber(watchedWidth))
    ) {
      setValue(`${FIELD_PATH}.${index}.width`, commodity.width, {
        shouldDirty: true,
      });
    }
    if (
      commodity.height &&
      (!preserveExisting || !isValidNumber(watchedHeight))
    ) {
      setValue(`${FIELD_PATH}.${index}.height`, commodity.height, {
        shouldDirty: true,
      });
    }
    if (
      commodity.weightAmount &&
      commodity.weightAmount > 0 &&
      (!preserveExisting || !isValidNumber(watchedWeight))
    ) {
      setValue(`${FIELD_PATH}.${index}.weight`, commodity.weightAmount, {
        shouldDirty: true,
      });
    }
    if (
      commodity.handlingUnitCount &&
      commodity.handlingUnitCount > 0 &&
      (!preserveExisting || !isValidNumber(watchedHandlingUnits))
    ) {
      setValue(
        `${FIELD_PATH}.${index}.handlingUnits`,
        commodity.handlingUnitCount,
        {
          shouldDirty: true,
        }
      );
    }

    const commodityWeightUnit =
      commodity.weightUnit &&
      TURVO_WEIGHT_UNIT_TO_FORM_WEIGHT_UNIT[commodity.weightUnit];
    if (
      commodityWeightUnit &&
      (!preserveExisting || !watchedItem?.weightUnit)
    ) {
      setValue(`${FIELD_PATH}.${index}.weightUnit`, commodityWeightUnit, {
        shouldDirty: true,
      });
    }

    const commodityDimUnit =
      commodity.dimUnit &&
      TURVO_DIM_UNIT_TO_FORM_DIMENSION_UNIT[commodity.dimUnit];
    if (
      commodityDimUnit &&
      (!preserveExisting || !watchedItem?.dimensionUnit)
    ) {
      setValue(`${FIELD_PATH}.${index}.dimensionUnit`, commodityDimUnit, {
        shouldDirty: true,
      });
    }

    if (
      commodity.handlingUnitTypeId != null &&
      (!preserveExisting || !watchedItem?.handlingUnitType)
    ) {
      const mappedHandlingUnitType =
        TURVO_QTY_UNIT_TYPE_ID_TO_VALUE[Number(commodity.handlingUnitTypeId)];
      if (mappedHandlingUnitType) {
        setValue(
          `${FIELD_PATH}.${index}.handlingUnitType`,
          mappedHandlingUnitType,
          {
            shouldDirty: true,
          }
        );
      }
    }
  };

  const selectedCommodity = useMemo(() => {
    if (!watchedItem?.commodityExternalTmsId) {
      return undefined;
    }

    return commodities?.find(
      (commodity) =>
        commodity.externalTMSID === watchedItem.commodityExternalTmsId
    );
  }, [commodities, watchedItem?.commodityExternalTmsId]);

  useEffect(() => {
    const commodityExternalTmsId = watchedItem?.commodityExternalTmsId ?? '';

    if (!commodityExternalTmsId || !selectedCommodity) {
      autoAppliedCommodityIdRef.current = '';
      return;
    }

    if (autoAppliedCommodityIdRef.current === commodityExternalTmsId) {
      return;
    }

    applyCommoditySelection(selectedCommodity, { preserveExisting: true });
    autoAppliedCommodityIdRef.current = commodityExternalTmsId;
  }, [selectedCommodity, watchedItem?.commodityExternalTmsId]);

  const descriptionOptions = useMemo<DescriptionOption[]>(() => {
    const options: DescriptionOption[] = (commodities ?? []).map(
      (commodity) => {
        const displayName =
          commodity.commodityDescription ??
          commodity.commodity ??
          commodity.externalTMSID;

        return {
          label: displayName,
          value: commodity.externalTMSID,
          commodityExternalTmsId: commodity.externalTMSID,
        };
      }
    );

    const normalizedSearch = descriptionSearchValue.trim();
    const normalizedDescription = descriptionValue.trim().toLowerCase();
    const hasMatchingCommodityOption = normalizedDescription
      ? options.some(
          (option) =>
            String(option.label ?? '')
              .trim()
              .toLowerCase() === normalizedDescription
        )
      : false;

    const customOption =
      descriptionValue && !hasMatchingCommodityOption
        ? {
            label: descriptionValue,
            value: `custom:${descriptionValue}`,
            isCustom: true,
            renderLabel: descriptionValue,
          }
        : null;
    const searchOption = normalizedSearch
      ? {
          label: normalizedSearch,
          value: `custom:${normalizedSearch}`,
          isCustom: true,
          renderLabel: `Use "${normalizedSearch}"`,
        }
      : null;

    if (
      customOption &&
      !options.some((option) => String(option.value) === customOption.value)
    ) {
      options.unshift(customOption);
    }

    if (
      searchOption &&
      !options.some((option) => String(option.value) === searchOption.value)
    ) {
      options.unshift(searchOption);
    }

    return options;
  }, [commodities, descriptionSearchValue, descriptionValue]);

  const descriptionSelectValue = selectedCommodity?.externalTMSID
    ? selectedCommodity.externalTMSID
    : descriptionValue
      ? `custom:${descriptionValue}`
      : undefined;

  const handleDescriptionSearch = (search: string) => {
    const normalizedSearch = search.trim();
    setDescriptionSearchValue(search);

    if (normalizedSearch && watchedItem?.commodityExternalTmsId) {
      setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, '', {
        shouldDirty: true,
      });
    }
  };

  const handleDescriptionSelect = (
    value: SelectValueType,
    option?: BaseOptionType | BaseOptionType[]
  ) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const selectedValue =
      typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : '';
    if (selectedOption?.isCustom) {
      const customValue = String(selectedOption.label ?? '').trim();
      setValue(`${FIELD_PATH}.${index}.description`, customValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${FIELD_PATH}.${index}.commodityExternalTmsId`, '', {
        shouldValidate: true,
        shouldDirty: true,
      });
      setDescriptionSearchValue('');
      return;
    }

    const selectedCommodity = (commodities ?? []).find(
      (commodity) => commodity.externalTMSID === selectedValue
    );

    if (selectedCommodity) {
      applyCommoditySelection(selectedCommodity);
    } else {
      setValue(
        `${FIELD_PATH}.${index}.description`,
        String(selectedOption?.label ?? selectedValue).trim(),
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
    }

    setDescriptionSearchValue('');
  };

  const descriptionPlaceholder = !hasCustomer
    ? 'Select customer first'
    : !hasTmsId
      ? 'Turvo integration unavailable'
      : !hasCustomerId
        ? 'Customer external ID unavailable'
        : isLoadingCommodities
          ? 'Searching commodities...'
          : 'Search descriptions';

  useEffect(() => {
    if (isEmptyItem && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyItem, isEditing]);

  useAutoOpenSplitSideLoadRow({
    watchedItem,
    isEditing,
    setIsEditing,
  });

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        Object.keys(originalValues).forEach((key) => {
          const fieldName =
            `${FIELD_PATH}.${index}.${key}` as keyof QuickQuoteInputs;
          const value = originalValues[key as keyof LTLItem];
          if (value !== undefined) {
            setValue(fieldName as any, value, { shouldValidate: true });
          }
        });
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: onRemove,
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isEditing) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div key='editing-actions' {...ITEM_CARD_ANIMATIONS.actions}>
          <Flex gap='sm'>
            {!isEmptyItem ? (
              <>
                <ActionButton
                  onClick={handlers.save}
                  className='hover:bg-info-200 text-info-600'
                  icon={CheckIcon}
                  aria-label='Save changes'
                />
                <ActionButton
                  onClick={handlers.discard}
                  className='hover:bg-error-200 text-error-600'
                  icon={XIcon}
                  aria-label='Discard changes'
                />
              </>
            ) : (
              <ActionButton
                onClick={handlers.remove}
                className='hover:bg-error-300 text-error-600'
                icon={TrashIcon}
                aria-label='Remove item'
              />
            )}
          </Flex>
        </motion.div>
      ) : (
        <motion.div key='view-actions' {...ITEM_CARD_ANIMATIONS.actions}>
          <Flex gap='sm'>
            <ActionButton
              onClick={handlers.edit}
              className='hover:bg-info-300 text-info-600'
              icon={PencilIcon}
              aria-label='Edit item'
            />
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-400 text-error-600'
              icon={TrashIcon}
              aria-label='Remove item'
            />
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ITEM_CARD_ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Flex align='center' gap='sm' className='mr-2'>
          <Typography
            variant='body-sm'
            weight='medium'
            className='whitespace-nowrap shrink-0'
          >
            Edit Item
          </Typography>
          {renderSideLoadStatusTag('max-w-[150px]')}
        </Flex>
        <Flex onClick={(e) => e.stopPropagation()} grow='none' shrink='0'>
          {renderActions()}
        </Flex>
      </Flex>

      <Grid cols='1' gap='xs' className='w-full mx-0 mb-1 items-start'>
        <Flex direction='col'>
          <Label name={`${FIELD_PATH}.${index}.description`} required>
            Description
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.description`}
            control={control}
            rules={{ required: 'Description is required' }}
            render={() => (
              <Select
                value={descriptionSelectValue}
                onChange={handleDescriptionSelect}
                options={descriptionOptions}
                showSearch
                onSearch={handleDescriptionSearch}
                placeholder={descriptionPlaceholder}
                className='w-full mt-1'
                filterOption={false}
                optionRender={(option: BaseOptionType) => {
                  const typedOption = option as DescriptionOption;
                  return (
                    <span className='block whitespace-normal break-words'>
                      {typedOption.renderLabel ?? typedOption.label}
                    </span>
                  );
                }}
              />
            )}
          />
          <Typography variant='body-xs' className='text-error-500 mt-1 min-h-4'>
            {formMethods.formState.errors.ltl?.items?.[index]?.description
              ?.message ?? ''}
          </Typography>
        </Flex>
      </Grid>

      <Grid
        gap='xs'
        className='w-full mx-0 mb-1 items-start grid-cols-[1fr_2fr_2fr]'
      >
        <Flex direction='col' className='min-w-0'>
          <RHFCommitOnBlurNumberInput
            name={`${FIELD_PATH}.${index}.handlingUnits`}
            label='Qty'
            placeholder='1'
            aiIconOnly={true}
            required={true}
            step={1}
            rules={{
              required: 'Required',
              min: { value: 1, message: 'Required' },
            }}
            inputClassName='mt-0 h-8 w-full min-w-0'
            data-testid={`${FIELD_PATH}.${index}.handlingUnits`}
          />
        </Flex>

        <Flex direction='col' className='min-w-0'>
          <Label name={`${FIELD_PATH}.${index}.handlingUnitType`} required>
            Units
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.handlingUnitType`}
            control={control}
            defaultValue={TURVO_QTY_UNIT_DEFAULT}
            rules={{ required: 'Units is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? TURVO_QTY_UNIT_DEFAULT}
                >
                  <SelectTrigger className='w-full mt-1'>
                    <SelectValue placeholder='Units' />
                  </SelectTrigger>
                  <SelectContent>
                    {TURVO_QTY_UNIT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Typography
                  variant='body-xs'
                  className='text-error-500 mt-1 min-h-4'
                >
                  {fieldState.error?.message ?? ''}
                </Typography>
              </>
            )}
          />
        </Flex>

        <Flex direction='col' className='min-w-0'>
          <Label name={`${FIELD_PATH}.${index}.dimensionUnit`} required>
            Dim. units
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.dimensionUnit`}
            control={control}
            defaultValue={dimensionUnitValue}
            rules={{ required: 'Dim. units is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ''}
                >
                  <SelectTrigger className='w-full mt-1'>
                    <SelectValue placeholder='Units' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='Inch'>Inch</SelectItem>
                    <SelectItem value='Centimeter'>Centimeter</SelectItem>
                    <SelectItem value='Foot'>Foot</SelectItem>
                    <SelectItem value='Meter'>Meter</SelectItem>
                    <SelectItem value='Millimeter'>Millimeter</SelectItem>
                    <SelectItem value='Yard'>Yard</SelectItem>
                  </SelectContent>
                </Select>

                <Typography
                  variant='body-xs'
                  className='text-error-500 mt-1 min-h-4'
                >
                  {fieldState.error?.message ?? ''}
                </Typography>
              </>
            )}
          />
        </Flex>
      </Grid>

      <Grid cols='3' gap='xs' className='w-full mx-0 mb-6 items-start'>
        <Flex direction='col'>
          <RHFCommitOnBlurNumberInput
            name={`${FIELD_PATH}.${index}.length`}
            label='Length'
            placeholder='0'
            aiIconOnly={true}
            required={true}
            step={1}
            rules={{
              required: 'Length is required',
              min: { value: 0, message: 'Length is required' },
            }}
            inlineDimensionUnit={dimensionUnit}
            data-testid={`${FIELD_PATH}.${index}.length`}
          />
        </Flex>
        <Flex direction='col'>
          <RHFCommitOnBlurNumberInput
            name={`${FIELD_PATH}.${index}.width`}
            label='Width'
            placeholder='0'
            aiIconOnly={true}
            required={true}
            step={1}
            rules={{
              required: 'Width is required',
              min: { value: 0, message: 'Width is required' },
            }}
            inlineDimensionUnit={dimensionUnit}
            data-testid={`${FIELD_PATH}.${index}.width`}
          />
        </Flex>
        <Flex direction='col'>
          <RHFCommitOnBlurNumberInput
            name={`${FIELD_PATH}.${index}.height`}
            label='Height'
            placeholder='0'
            aiIconOnly={true}
            required={true}
            step={1}
            rules={{
              required: 'Height is required',
              min: { value: 0, message: 'Height is required' },
            }}
            inlineDimensionUnit={dimensionUnit}
            data-testid={`${FIELD_PATH}.${index}.height`}
          />
        </Flex>
      </Grid>

      <Grid
        gap='xs'
        className='w-full mx-0 mb-2 items-start grid-cols-[3fr_3fr_2fr]'
      >
        <Flex direction='col' className='min-w-0'>
          <RHFTextInput
            name={`${FIELD_PATH}.${index}.weight`}
            label='Total Wt.'
            placeholder='0'
            inputType='number'
            aiIconOnly={true}
            options={{
              valueAsNumber: true,
              min: 0,
              required: 'Total weight is required',
            }}
            required
            inlineDimensionUnit={weightUnit}
            step={1}
          />
        </Flex>
        <Flex direction='col' className='min-w-0'>
          <Label name={`${FIELD_PATH}.${index}.weightUnit`} required>
            Wt. unit
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.weightUnit`}
            control={control}
            defaultValue={weightUnitValue}
            rules={{ required: 'Weight units is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ''}
                >
                  <SelectTrigger className='w-full mt-1'>
                    <SelectValue placeholder='Units' />
                  </SelectTrigger>
                  <SelectContent>
                    {LTL_WEIGHT_UNIT_OPTIONS.map((unit: string) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Typography
                  variant='body-xs'
                  className='text-error-500 mt-1 min-h-4'
                >
                  {fieldState.error?.message ?? ''}
                </Typography>
              </>
            )}
          />
        </Flex>
        <Flex direction='col' className='min-w-0'>
          <Label name={`${FIELD_PATH}.${index}.density`}>Density</Label>
          <Input
            readOnly
            value={densityValue}
            className='mt-1 h-8 w-full min-w-0 cursor-default bg-neutral-input-disabled-bg'
          />
        </Flex>
      </Grid>

      <Grid cols='2' gap='xs' className='w-full mx-0 mb-2 items-start'>
        <Flex direction='col' className='w-full min-w-0'>
          <Label name={`${FIELD_PATH}.${index}.nmfcCode`} className='min-h-6'>
            NMFC #
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.nmfcCode`}
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                className='mt-1 h-8 w-full min-w-0'
              />
            )}
          />
        </Flex>
        <Flex direction='col' className='w-full min-w-0'>
          <Label
            name={`${FIELD_PATH}.${index}.freightClass`}
            required
            aiIconOnly={true}
            className='min-h-6'
          >
            Freight Class
          </Label>
          <Controller
            name={`${FIELD_PATH}.${index}.freightClass`}
            control={control}
            rules={{ required: 'Freight class is required' }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ''}
                >
                  <SelectTrigger className='w-full mt-1'>
                    <SelectValue
                      placeholder={
                        isCalculatingFreightClass
                          ? 'Calculating freight class...'
                          : 'Select freight class'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {LTL_FREIGHT_CLASSES.map((cls) => (
                      <SelectItem key={cls} value={cls}>
                        {cls}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Typography
                  variant='body-xs'
                  className='text-error-500 mt-1 min-h-4'
                >
                  {fieldState.error?.message ?? ''}
                </Typography>
              </>
            )}
          />
        </Flex>
      </Grid>
    </motion.div>
  );

  const renderViewMode = () => {
    if (!watchedItem?.description) {
      return null;
    }

    const hasQty = isValidNumber(watchedItem.handlingUnits);
    const hasWeight = isValidNumber(watchedItem.weight);
    const hasUnits = Boolean(watchedItem.handlingUnitType);
    const hasNmfcCode = Boolean(watchedItem.nmfcCode);
    const hasFreightClass = Boolean(watchedItem.freightClass);
    const dimensions = [
      watchedItem.length,
      watchedItem.width,
      watchedItem.height,
    ].filter(isValidNumber);
    const hasDimensions = dimensions.length > 0;

    const parts: JSX.Element[] = [];
    if (hasQty) {
      parts.push(
        <Typography key='qty' variant='body-xs' className='text-neutral-500'>
          Qty: {watchedItem.handlingUnits}
        </Typography>
      );
    }
    if (hasDimensions) {
      parts.push(
        <Typography key='dims' variant='body-xs' className='text-neutral-500'>
          {dimensions.join(' × ')} {dimensionUnit}
        </Typography>
      );
    }
    if (hasWeight) {
      parts.push(
        <Typography key='wt' variant='body-xs' className='text-neutral-500'>
          {watchedItem.weight} {weightUnit}
        </Typography>
      );
    }
    if (hasUnits) {
      const unitKey =
        watchedItem.handlingUnitType as keyof typeof TURVO_QTY_UNIT_LABEL_BY_KEY;
      const unitLabel =
        unitKey && unitKey in TURVO_QTY_UNIT_LABEL_BY_KEY
          ? TURVO_QTY_UNIT_LABEL_BY_KEY[unitKey]
          : watchedItem.handlingUnitType;

      parts.push(
        <Typography key='units' variant='body-xs' className='text-neutral-500'>
          {unitLabel}
        </Typography>
      );
    }
    if (hasNmfcCode) {
      parts.push(
        <Typography key='nmfc' variant='body-xs' className='text-neutral-500'>
          NMFC {watchedItem.nmfcCode}
        </Typography>
      );
    }
    if (hasFreightClass) {
      parts.push(
        <Typography
          key='freight-class'
          variant='body-xs'
          className='text-neutral-500'
        >
          Class {watchedItem.freightClass}
        </Typography>
      );
    }

    return (
      <motion.div
        key='view-mode'
        {...ITEM_CARD_ANIMATIONS.toggle}
        className='overflow-hidden cursor-pointer'
        onClick={handlers.edit}
        onKeyDown={(e: React.KeyboardEvent) =>
          e.key === 'Enter' && handlers.edit()
        }
        role='button'
        tabIndex={0}
        aria-label='Click to edit item'
      >
        <Flex justify='between' align='center' className='min-w-0'>
          <Flex align='center' gap='sm' className='flex-1 min-w-0 mr-2'>
            <Package className='h-4 w-4 text-neutral-500 shrink-0' />
            <Typography
              variant='body-sm'
              weight='medium'
              className='truncate'
              title={watchedItem.description}
            >
              {watchedItem.description}
            </Typography>
            {renderSideLoadStatusTag('max-w-[170px]')}
          </Flex>
          <div onClick={(e) => e.stopPropagation()} className='shrink-0'>
            {renderActions()}
          </div>
        </Flex>
        {parts.length > 0 && (
          <Flex align='center' gap='sm' className='mt-1 ml-1 flex-wrap'>
            {parts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className='text-neutral-300'>•</span>}
                {p}
              </React.Fragment>
            ))}
          </Flex>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ITEM_CARD_ANIMATIONS.card}
      className='w-full overflow-hidden rounded-md border border-neutral-400 shadow-sm relative p-3 mb-2'
      style={
        showVisualizationColor
          ? {
              borderLeft: `3px solid ${getColorForItemId(
                watchedItem?.itemId ?? `item-${index}`,
                itemColorMap
              )}`,
            }
          : undefined
      }
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>
    </motion.div>
  );
}
